import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { MaskLines, Reveal } from "../components/motion";
import { RichText } from "../components/RichText";
import { ShareBar } from "../components/ShareBar";
import { ArticleCard } from "../components/ArticleCard";
import { Attachments, NotFoundBlock } from "./ProductDetail";
import { asList, mediaUrl, useFetch } from "../lib/api";
import { ease, fmtDate } from "../lib/content";

const Related = ({ current }) => {
  const { data } = useFetch("/articles");
  const items = asList(data).filter((a) => a.slug !== current).slice(0, 3);
  if (!items.length) return null;
  return (
    <section className="border-t border-teal/15 bg-paper py-20">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <p className="label-caps text-moss">Keep reading</p>
        <div className="mt-12 grid gap-x-8 gap-y-16 md:grid-cols-2 lg:grid-cols-3">
          {items.map((a, i) => (
            <ArticleCard key={a.id} article={a} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default function ArticleDetail() {
  const { slug } = useParams();
  const { data: a, loading, error } = useFetch(`/articles/${slug}`);
  if (loading) return <main className="min-h-screen bg-teal" />;
  if (error || !a) return <NotFoundBlock to="/insights" label="Back to insights" />;

  return (
    <main data-testid="article-detail-page">
      <section className="grain relative bg-teal pb-40 pt-32 text-mist sm:pt-40">
        <div className="relative mx-auto max-w-[1100px] px-5 sm:px-8">
          <Link to="/insights" data-testid="back-to-insights" className="label-caps inline-flex items-center gap-2 !text-[11px] text-mist/80 hover:text-mist">
            <ArrowLeft className="h-4 w-4" /> All insights
          </Link>
          <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.16em] text-sage">
            {a.category} · {fmtDate(a.published_at)} · {a.read_minutes ?? 5} min read
          </p>
          <MaskLines as="h1" onLoad delay={0.1} lines={[a.title]} testId="article-title" className="mt-6 font-display text-4xl font-normal leading-[1.05] tracking-[-0.02em] sm:text-6xl" />
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-mist/85">{a.excerpt}</p>
        </div>
      </section>
      {a.cover_image && (
        <div className="relative z-10 mx-auto -mt-28 max-w-[1100px] px-5 sm:px-8">
          <motion.div className="aspect-[16/9] overflow-hidden rounded-t-[999px] bg-mist sm:rounded-t-[400px]" initial={{ clipPath: "inset(30% 10% 0 10%)" }} animate={{ clipPath: "inset(0% 0% 0 0%)" }} transition={{ duration: 1.4, ease, delay: 0.3 }}>
            <img src={mediaUrl(a.cover_image)} alt={a.title} className="h-full w-full object-cover" />
          </motion.div>
        </div>
      )}
      <section className="bg-paper py-16 sm:py-24">
        <div className="mx-auto grid max-w-[1100px] gap-14 px-5 sm:px-8 lg:grid-cols-12">
          <aside className="space-y-8 lg:col-span-4 lg:order-2">
            <div className="space-y-8 lg:sticky lg:top-28">
              <div>
                <p className="label-caps text-moss">Written by</p>
                <p className="mt-3 font-display text-lg text-teal" data-testid="article-author">{a.author}</p>
              </div>
              {(a.attachments || []).length > 0 && (
                <div>
                  <p className="label-caps mb-3 text-moss">Downloads</p>
                  <Attachments items={a.attachments} testPrefix="article-attachment" />
                </div>
              )}
              {a.source_url && (
                <a href={a.source_url} target="_blank" rel="noopener noreferrer" data-testid="article-source-link" className="group inline-flex items-center gap-2 text-sm text-teal underline decoration-sage underline-offset-4">
                  Read the original source <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
                </a>
              )}
              <div>
                <p className="label-caps mb-3 text-moss">Share</p>
                <ShareBar title={a.title} />
              </div>
            </div>
          </aside>
          <Reveal className="lg:col-span-8 lg:order-1">
            <RichText text={a.body} />
          </Reveal>
        </div>
      </section>
      <Related current={a.slug} />
    </main>
  );
}
